import { z } from 'zod';
import { TIMESHEET_STATES } from '../enums';

/** ISO date string (date only — no time). Format: YYYY-MM-DD */
export const isoDateSchema = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, 'Date must be in YYYY-MM-DD format');

/**
 * Create / update a single timesheet entry.
 * Hours are constrained to 15-minute increments — matches industry standard
 * for time-based billing and reduces UI selector complexity.
 */
export const timesheetEntryInputSchema = z.object({
  workDate: isoDateSchema,
  clientId: z.string().uuid(),
  engagementId: z.string().uuid(),
  activityId: z.string().uuid(),
  hours: z
    .number()
    .min(0.25, 'Minimum is 0.25 hours (15 minutes)')
    .max(24, 'Cannot exceed 24 hours per entry')
    .multipleOf(0.25, 'Hours must be in 15-minute increments'),
  description: z.string().max(500).optional(),
});
export type TimesheetEntryInput = z.infer<typeof timesheetEntryInputSchema>;

export const timesheetStateSchema = z.enum(TIMESHEET_STATES);

/**
 * Bulk state transition — approve/reject multiple entries at once.
 * Server validates each entry's current state allows the requested transition.
 */
export const timesheetTransitionInputSchema = z.object({
  entryIds: z.array(z.string().uuid()).min(1).max(500),
  toState: timesheetStateSchema,
  comment: z.string().max(500).optional(),
});
export type TimesheetTransitionInput = z.infer<typeof timesheetTransitionInputSchema>;

/**
 * Read filter — used by GET /timesheet/entries.
 * Defaults are applied server-side if omitted.
 */
export const timesheetFilterSchema = z.object({
  /** Start of range (inclusive). */
  fromDate: isoDateSchema.optional(),
  /** End of range (inclusive). */
  toDate: isoDateSchema.optional(),
  /** Filter by owner. If omitted, defaults to current user (per RBAC). */
  ownerId: z.string().uuid().optional(),
  state: timesheetStateSchema.optional(),
  clientId: z.string().uuid().optional(),
});
export type TimesheetFilter = z.infer<typeof timesheetFilterSchema>;
